import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Text, VStack, Button, Code } from '@chakra-ui/react';
import { ShieldX } from 'lucide-react';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { POKER_COLORS } from '../../theme/designSystem';
import gameApiService from '../../services/gameApiService';
import { isEmulatorEnv } from '../../utils/apiBase';

// Optional: legacy hardcoded admin UID (kept for backward compat). Real check
// is the isAdmin/role field on the users doc, written by emulator bootstrap
// and the server-side admin promotion flow.
const ADMIN_UID = process.env.REACT_APP_ADMIN_UID;

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * AdminRoute - Protected route that only allows admin access.
 * Admin check matches the server's verifyAdminInline: users/{uid}.isAdmin === true
 * OR users/{uid}.role === 'admin'. Also honors the legacy REACT_APP_ADMIN_UID
 * env var so a freshly-deployed env without the field still works.
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<{ uid?: string; isAdminField?: any; roleField?: any; docExists?: boolean }>({});
  const [bootstrapping, setBootstrapping] = useState(false);
  // Shared detection with apiBase (env flag OR localhost auto-detect) so the
  // emulator bootstrap option appears whenever API calls go to the emulator.
  const isEmulator = isEmulatorEnv();

  const checkAdmin = async () => {
    const user = auth.currentUser;
    if (!user) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    // Cheap path: env var UID match (legacy)
    if (ADMIN_UID && user.uid === ADMIN_UID) {
      setIsAdmin(true);
      setDebugInfo({ uid: user.uid, isAdminField: 'env-match', roleField: '-', docExists: true });
      setLoading(false);
      return;
    }

    // Authoritative path: read isAdmin/role from the user doc
    try {
      const snap = await getDoc(doc(db, 'users', user.uid));
      if (snap.exists()) {
        const data = snap.data();
        const ok = data.isAdmin === true || data.role === 'admin';
        setIsAdmin(ok);
        setDebugInfo({
          uid: user.uid,
          isAdminField: data.isAdmin,
          roleField: data.role,
          docExists: true,
        });
      } else {
        setIsAdmin(false);
        setDebugInfo({ uid: user.uid, docExists: false });
      }
    } catch (err) {
      console.error('[AdminRoute] user doc read failed:', err);
      setIsAdmin(false);
      setDebugInfo({ uid: user.uid, isAdminField: `error: ${(err as Error).message}` });
    }
    setLoading(false);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(() => { checkAdmin(); });
    return unsubscribe;
  }, []);

  const handleEmulatorBootstrap = async () => {
    setBootstrapping(true);
    const r = await gameApiService.emulatorBootstrap();
    if (!r.success) {
      setBootstrapping(false);
      alert(r.error || 'Bootstrap failed');
      return;
    }
    // Force fresh token + doc re-read
    try { await auth.currentUser?.getIdToken(true); } catch {}
    // Hard reload to flush AuthContext too
    window.location.href = '/admin';
  };

  if (loading) {
    return (
      <Box
        w="100%"
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#0D1B2A"
      >
        <Box fontSize="lg" color="#00D9D9">
          Checking permissions...
        </Box>
      </Box>
    );
  }

  // Not authenticated - redirect to login
  if (isAdmin === null) {
    return <Navigate to="/login" replace />;
  }

  // Not admin - show access denied
  if (!isAdmin) {
    return (
      <Box
        w="100%"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#0D1B2A"
        p={8}
      >
        <VStack spacing={6} textAlign="center" maxW="560px">
          <Box
            p={4}
            borderRadius="full"
            bg={`${POKER_COLORS.darkRed}22`}
            border={`2px solid ${POKER_COLORS.darkRed}`}
          >
            <ShieldX size={48} color={POKER_COLORS.darkRed} />
          </Box>
          <Text color={POKER_COLORS.darkRed} fontSize="2xl" fontWeight="bold">
            Access Denied
          </Text>
          <Text color="gray.400">
            You don't have permission to access the admin dashboard.
            This area is restricted to administrators only.
          </Text>

          {/* Emulator-only bootstrap so you can self-promote and continue */}
          {isEmulator && (
            <Box
              w="100%"
              bg="rgba(251, 146, 60, 0.08)"
              border="1px solid rgba(251, 146, 60, 0.4)"
              borderRadius="md"
              p={4}
            >
              <Text color="orange.300" fontSize="sm" fontWeight="bold" mb={2}>
                 Emulator detected

              </Text>
              <Text color="gray.400" fontSize="xs" mb={3}>
                Click below to promote yourself to admin and reload. Refuses
                to run in production deploys.
              </Text>
              <Button
                bg="orange.600"
                color="white"
                _hover={{ bg: 'orange.500' }}
                onClick={handleEmulatorBootstrap}
                isLoading={bootstrapping}
                loadingText="Promoting"
                size="sm"
              >
                Make me admin (emulator)
              </Button>
            </Box>
          )}

          {/* Debug info - visible to everyone landing here so we can diagnose */}
          <Box
            w="100%"
            bg="rgba(255,255,255,0.03)"
            border="1px solid rgba(255,255,255,0.08)"
            borderRadius="md"
            p={3}
            fontSize="xs"
            textAlign="left"
            color="gray.400"
          >
            <Text color="gray.500" mb={1} fontWeight="bold">DIAGNOSTIC</Text>
            <Text>UID: <Code fontSize="xs" bg="transparent" color="cyan.300">{debugInfo.uid || '(not signed in)'}</Code></Text>
            <Text>users doc exists: <Code fontSize="xs" bg="transparent">{String(debugInfo.docExists)}</Code></Text>
            <Text>isAdmin field: <Code fontSize="xs" bg="transparent">{String(debugInfo.isAdminField)}</Code></Text>
            <Text>role field: <Code fontSize="xs" bg="transparent">{String(debugInfo.roleField)}</Code></Text>
            <Text>env ADMIN_UID set: <Code fontSize="xs" bg="transparent">{String(!!ADMIN_UID)}</Code></Text>
          </Box>

          <Text
            color={POKER_COLORS.cyan}
            fontSize="sm"
            cursor="pointer"
            onClick={() => window.location.href = '/'}
            _hover={{ textDecoration: 'underline' }}
          >
            ← Return to Home
          </Text>
        </VStack>
      </Box>
    );
  }

  // Is admin - render children
  return <>{children}</>;
};

export default AdminRoute;

/**
 * Helper function to check if current user is admin (env-var-only fast path,
 * does NOT do a Firestore read). For accurate checks prefer the AdminRoute
 * component or read the users/{uid} doc directly.
 */
export const isCurrentUserAdmin = (): boolean => {
  const currentUser = auth.currentUser;
  if (!currentUser || !ADMIN_UID) return false;
  return currentUser.uid === ADMIN_UID;
};

/**
 * Get admin UID (for debugging)
 */
export const getAdminUID = (): string | undefined => {
  return ADMIN_UID;
};